import errorHandler from './utils/errorHandler';
import React, { useState, useEffect } from 'react';
import { Paper, Typography, Snackbar, Alert, Button, Box, Divider, Chip } from '@mui/material';
import api from './axiosConfig';
import SearchEmployee from './SearchEmployee';
import ReviewForm from './ReviewForm';
import EmployeeReviews from './EmployeeReviews';
import { useAuth } from './AuthContext';

const EMPTY_REVIEW = {
  employeeName: '',
  jobTitle: '',
  department: '',
  dateOfEngagement: '',
  manager: '',
  dateOfMeeting: '',
  objectives: '',
  achievements: '',
  strengths: '',
  needsImprovement: '',
  developmentActivities: '',
  careerPlanning: '',
  otherDiscussionPoints: '',
  clientService: [],
  retailSales: [],
  workManagement: [],
  teamwork: [],
  attendancePunctuality: [],
  communication: [],
  overallPerformanceRating: [],
  supervisorsComments: '',
  employeeComments: '',
  actionPlans: '',
  employeeSignature: '',
  appraiserSignature: '',
  reviewDate: '',
  reviewingManagerSignature: '',
  reviewingManagerDate: ''
};

// The backend model stores each checkbox group as individual boolean
// columns (e.g. client_service_meets_targets), not as arrays. These maps
// translate ReviewForm's array-based UI state into the flat field names
// EmployeeReview expects, and back again when loading an existing draft.
const CHECKBOX_FIELD_MAP = {
  clientService: {
    meetsTargets: 'client_service_meets_targets',
    communicatesEffectively: 'client_service_communicates_effectively',
    deliversHighQuality: 'client_service_high_quality_service',
  },
  retailSales: {
    recommendsProducts: 'retail_sales_recommends_products',
    achievesSalesTargets: 'retail_sales_meets_sales_targets',
    providesProductKnowledge: 'retail_sales_product_knowledge',
  },
  workManagement: {
    managesWorkload: 'work_management_manages_workload',
    prioritizesTasks: 'work_management_prioritizes_tasks',
    meetsClientDemand: 'work_management_meets_client_demand',
  },
  teamwork: {
    worksCooperatively: 'teamwork_works_cooperatively',
    contributesPositively: 'teamwork_positive_contribution',
    supportsTeam: 'teamwork_supports_team',
  },
  attendancePunctuality: {
    consistentAttendance: 'attendance_consistent_attendance',
    arrivesOnTime: 'attendance_on_time',
    communicatesAbsences: 'attendance_communicates_absences',
  },
  communication: {
    communicatesEffectively: 'communication_effective_communication',
    activelyListens: 'communication_active_listening',
    explainsServicesClearly: 'communication_clear_explanation',
  },
  overallPerformanceRating: {
    outstanding: 'overall_performance_outstanding',
    meetsExpectations: 'overall_performance_meets_expectations',
    needsImprovement: 'overall_performance_needs_improvement',
    unsatisfactory: 'overall_performance_unsatisfactory',
  },
};

function toBackendPayload(reviewData) {
  const payload = {
    job_title: reviewData.jobTitle,
    department: reviewData.department,
    date_of_engagement: reviewData.dateOfEngagement || null,
    manager: reviewData.manager,
    date_of_meeting: reviewData.dateOfMeeting || null,
    objectives: reviewData.objectives,
    strengths: reviewData.strengths,
    needs_improvement: reviewData.needsImprovement,
    development_activities: reviewData.developmentActivities,
    career_planning: reviewData.careerPlanning,
    other_discussion_points: reviewData.otherDiscussionPoints,
    supervisor_comments: reviewData.supervisorsComments,
    employee_comments: reviewData.employeeComments,
    action_plans: reviewData.actionPlans,
    comments: reviewData.comments,
    performance_score: reviewData.performance_score ?? null,
    employee_signature: reviewData.employeeSignature,
    appraiser_signature: reviewData.appraiserSignature,
    review_date: reviewData.reviewDate || null,
    reviewing_manager_signature: reviewData.reviewingManagerSignature,
    reviewing_manager_date: reviewData.reviewingManagerDate || null,
  };

  for (const [group, fieldMap] of Object.entries(CHECKBOX_FIELD_MAP)) {
    const selected = Array.isArray(reviewData[group]) ? reviewData[group] : [];
    for (const [uiKey, backendField] of Object.entries(fieldMap)) {
      payload[backendField] = selected.includes(uiKey);
    }
  }

  return payload;
}

function fromBackendReview(review) {
  const reviewData = {
    ...EMPTY_REVIEW,
    jobTitle: review.job_title || '',
    department: review.department || '',
    dateOfEngagement: review.date_of_engagement || '',
    manager: review.manager || '',
    dateOfMeeting: review.date_of_meeting || '',
    objectives: review.objectives || '',
    strengths: review.strengths || '',
    needsImprovement: review.needs_improvement || '',
    developmentActivities: review.development_activities || '',
    careerPlanning: review.career_planning || '',
    otherDiscussionPoints: review.other_discussion_points || '',
    supervisorsComments: review.supervisor_comments || '',
    employeeComments: review.employee_comments || '',
    actionPlans: review.action_plans || '',
    employeeSignature: review.employee_signature || '',
    appraiserSignature: review.appraiser_signature || '',
    reviewDate: review.review_date || '',
    reviewingManagerSignature: review.reviewing_manager_signature || '',
    reviewingManagerDate: review.reviewing_manager_date || '',
  };

  for (const [group, fieldMap] of Object.entries(CHECKBOX_FIELD_MAP)) {
    reviewData[group] = Object.entries(fieldMap)
      .filter(([, backendField]) => review[backendField])
      .map(([uiKey]) => uiKey);
  }

  return reviewData;
}

const SubmitEmployeeReview = () => {
  const { user } = useAuth();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null); // secure_id
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('');
  const [currentReviewId, setCurrentReviewId] = useState(null); // null until first save
  const [currentReviewStatus, setCurrentReviewStatus] = useState(null);
  const [reviewData, setReviewData] = useState(EMPTY_REVIEW);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [historyKey, setHistoryKey] = useState(0); // bump to force EmployeeReviews to refetch

  useEffect(() => {
    errorHandler.logDebug('SubmitEmployeeReview', 'SubmitEmployeeReview mounted');
    return () => errorHandler.logDebug('SubmitEmployeeReview', 'SubmitEmployeeReview unmounted');
  }, []);

  const handleEmployeeSelect = (employeeId, employeeName) => {
    errorHandler.logDebug('SubmitEmployeeReview', 'Employee selected:', employeeId);
    setSelectedEmployeeId(employeeId);
    setSelectedEmployeeName(employeeName || '');
    setCurrentReviewId(null);
    setCurrentReviewStatus(null);
    setReviewData({ ...EMPTY_REVIEW, employeeName: employeeName || '' });
    setError(null);
  };

  const handleStartNewDraft = () => {
    setCurrentReviewId(null);
    setCurrentReviewStatus(null);
    setReviewData({ ...EMPTY_REVIEW, employeeName: selectedEmployeeName });
    setError(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReviewData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleCheckboxChange = (section, value) => {
    setReviewData((prevData) => {
      const updatedSection = prevData[section].includes(value)
        ? prevData[section].filter((item) => item !== value)
        : [...prevData[section], value];
      return { ...prevData, [section]: updatedSection };
    });
  };

  const saveDraft = async () => {
    if (!selectedEmployeeId) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const payload = toBackendPayload(reviewData);
      let response;

      if (currentReviewId) {
        // Existing draft — update it. The backend rejects this once the
        // review has been submitted/locked, so this only ever succeeds
        // while status is 'initial'.
        response = await api.put(
          `/reviews/employee/${selectedEmployeeId}/${currentReviewId}`,
          payload
        );
      } else {
        // First save for this employee in this sitting — always creates a
        // brand new review row. Never overwrites a past review.
        response = await api.post(
          `/reviews/employee/${selectedEmployeeId}`,
          payload
        );
        setCurrentReviewId(response.data.id);
      }

      setCurrentReviewStatus(response.data.status);
      errorHandler.logDebug('SubmitEmployeeReview', 'Draft saved successfully', response.data);
      setSnackbar({ open: true, message: 'Draft saved', severity: 'success' });
    } catch (err) {
      const message = errorHandler.handleApiError(err, 'SubmitEmployeeReview.saveDraft');
      setError(message);
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitAndLockReview = async () => {
    if (!selectedEmployeeId || !currentReviewId) return;
    setIsLocking(true);
    setError(null);
    try {
      const response = await api.post(
        `/reviews/employee/${selectedEmployeeId}/${currentReviewId}/submit`
      );
      setCurrentReviewStatus(response.data.status);
      errorHandler.logDebug('SubmitEmployeeReview', 'Review locked', response.data);
      setSnackbar({ open: true, message: 'Review submitted and locked', severity: 'success' });
      setHistoryKey((k) => k + 1); // refresh the history list now a summary exists
    } catch (err) {
      const message = errorHandler.handleApiError(err, 'SubmitEmployeeReview.submitAndLockReview');
      setError(message);
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setIsLocking(false);
    }
  };

  const isLocked = currentReviewStatus && currentReviewStatus !== 'initial';

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>Employee Review</Typography>

      {!selectedEmployeeId ? (
        <>
          <Typography variant="body1" gutterBottom>
            Search and select an employee to view their review history or start a new review.
          </Typography>
          <SearchEmployee onEmployeeSelect={handleEmployeeSelect} />
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">{selectedEmployeeName}</Typography>
            <Button size="small" onClick={() => setSelectedEmployeeId(null)}>
              Choose a different employee
            </Button>
          </Box>

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Review history</Typography>
          <EmployeeReviews key={historyKey} employee={{ id: selectedEmployeeId }} />

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1">
              {currentReviewId ? 'Current review' : 'New review'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {currentReviewStatus && (
                <Chip
                  size="small"
                  label={currentReviewStatus === 'initial' ? 'Draft' : 'Submitted'}
                  color={currentReviewStatus === 'initial' ? 'default' : 'success'}
                />
              )}
              {currentReviewId && (
                <Button size="small" onClick={handleStartNewDraft} disabled={isSubmitting || isLocking}>
                  Start a new review instead
                </Button>
              )}
            </Box>
          </Box>

          {isLocked ? (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              This review has been submitted and is locked. Start a new review to record another meeting.
            </Typography>
          ) : (
            <>
              <ReviewForm
                reviewData={reviewData}
                handleChange={handleChange}
                handleCheckboxChange={handleCheckboxChange}
                isSubmitting={isSubmitting || isLocking}
                error={error}
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  onClick={saveDraft}
                  disabled={isSubmitting || isLocking}
                  variant="outlined"
                >
                  Save Draft
                </Button>
                <Button
                  onClick={submitAndLockReview}
                  disabled={!currentReviewId || isSubmitting || isLocking}
                  variant="contained"
                  color="primary"
                >
                  {isLocking ? 'Submitting…' : 'Submit & Lock Review'}
                </Button>
              </Box>
              {!currentReviewId && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Save a draft first — you can submit and lock it once it's saved.
                </Typography>
              )}
            </>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SubmitEmployeeReview;